// file: src/pages/pengamatan/persen-bunga/FormPersenBungaLogic.js
import { ref, get } from "firebase/database";
import { database } from "../../../firebase";

// Fungsi untuk memformat angka - menghapus angka di belakang titik
const formatAngka = (value) => {
    if (!value || value === "0") return "0";
    
    // Konversi ke string
    const stringValue = String(value);
    
    // Jika mengandung titik, ambil bagian sebelum titik
    if (stringValue.includes('.')) {
        return stringValue.split('.')[0];
    }
    
    return stringValue;
};

// Fungsi untuk memformat angka dengan pemisah ribuan
const formatAngkaRibuan = (value) => {
    if (!value || value === "0") return "0";
    
    // Konversi ke string dan ambil bagian sebelum titik (jika ada)
    let stringValue = String(value);
    if (stringValue.includes('.')) {
        stringValue = stringValue.split('.')[0];
    }
    
    // Format dengan pemisah ribuan
    return stringValue.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};

// Fungsi untuk menghitung persentase dengan 2 angka di belakang koma
const hitungPersentase = (pembilang, penyebut) => {
    // Konversi ke number dan pastikan valid
    const numPembilang = parseFloat(pembilang) || 0;
    const numPenyebut = parseFloat(penyebut) || 0;
    
    // Hindari pembagian dengan nol
    if (numPenyebut === 0) {
        return "0.00%";
    }
    
    // Hitung persentase
    const persentase = (numPembilang / numPenyebut) * 100;
    
    // Format dengan 2 angka di belakang koma
    return `${persentase.toFixed(2)}%`;
};

// Fungsi untuk mendapatkan tanggal pengamatan berdasarkan logika
export const getTanggalPengamatan = (groupData) => {
    if (!groupData || !groupData.items || groupData.items.length === 0) {
        return "";
    }

    // Jika hanya ada 1 data, langsung ambil tanggalnya
    if (groupData.items.length === 1) {
        return groupData.items[0].tanggal_pengamatan;
    }

    // Jika lebih dari 1 data, periksa apakah semua tanggal sama
    const firstDate = groupData.items[0].tanggal_pengamatan;
    const allSameDate = groupData.items.every(item => 
        item.tanggal_pengamatan === firstDate
    );

    if (allSameDate) {
        // Jika semua tanggal sama, tampilkan tanggal tersebut
        return firstDate;
    } else {
        // Jika tanggal berbeda, ambil tanggal terlama (terkecil)
        const sortedDates = groupData.items
            .map(item => {
                // Konversi format DD-MM-YYYY ke Date object untuk perbandingan
                const [day, month, year] = item.tanggal_pengamatan.split('-');
                return new Date(`${year}-${month}-${day}`);
            })
            .sort((a, b) => a - b); // Urutkan dari terlama ke terbaru
        
        // Ambil tanggal terlama (index 0) dan konversi kembali ke format DD-MM-YYYY
        const oldestDate = sortedDates[0];
        const day = String(oldestDate.getDate()).padStart(2, '0');
        const month = String(oldestDate.getMonth() + 1).padStart(2, '0');
        const year = oldestDate.getFullYear();
        
        return `${day}-${month}-${year}`;
    }
};

// Fungsi untuk mendapatkan bagian unit kerja (dikosongkan)
export const getBagianUnitKerja = (groupData) => {
    return "";
};

// Fungsi untuk mendapatkan PPN (dikosongkan)
export const getPPN = (groupData) => {
    return "";
};

// Fungsi untuk mendapatkan status lokasi
export const getStatusLokasi = async (groupData) => {
    if (!groupData || !groupData.id_lokasi) {
        return "Tidak Diketahui";
    }

    try {
        // Referensi ke tb_lokasi
        const lokasiRef = ref(database, "db_freshpine/tb_lokasi");
        
        // Ambil semua data lokasi
        const snapshot = await get(lokasiRef);
        
        if (snapshot.exists()) {
            const lokasiData = snapshot.val();
            
            // Cari lokasi dengan id_lokasi yang sesuai
            const foundLokasi = Object.values(lokasiData).find(
                lokasi => lokasi.id_lokasi === groupData.id_lokasi
            );
            
            if (foundLokasi && foundLokasi.status) {
                return foundLokasi.status;
            }
        }
        
        return "Tidak Diketahui";
    } catch (error) {
        console.error("Error fetching status lokasi:", error);
        return "Error";
    }
};

// Fungsi untuk mendapatkan semua nama pengamat
export const getNamaPengamat = (groupData) => {
    if (!groupData || !groupData.items || groupData.items.length === 0) {
        return "";
    }

    // Ambil semua nama pengamat unik dari semua data
    const allPengamat = groupData.items.map(item => item.nama_pengamat || "");
    
    // Filter untuk menghapus nilai kosong dan duplikat
    const uniquePengamat = [...new Set(allPengamat.filter(pengamat => pengamat.trim() !== ""))];
    
    // Gabungkan semua nama pengamat dengan pemisah koma
    return uniquePengamat.join(", ");
};

// Fungsi untuk mendapatkan nama pengamat pertama saja
export const getNamaPengamatPertama = (groupData) => {
    if (!groupData || !groupData.items || groupData.items.length === 0) {
        return "-";
    }
    
    // Ambil nama pengamat dari data pertama
    return groupData.items[0].nama_pengamat || "-";
};

// Fungsi untuk mendapatkan nama mandor
export const getNamaMandor = (groupData) => {
    if (!groupData) {
        return "-";
    }
    
    return groupData.nama_mandor || "-";
};

// Fungsi untuk mendapatkan semua data plot dari groupData
export const getAllPlotData = (groupData) => {
    if (!groupData || !groupData.items || groupData.items.length === 0) {
        return [];
    }

    const allPlots = [];

    // Iterasi melalui semua items dalam groupData
    groupData.items.forEach(item => {
        if (item.originalData && item.originalData.plot) {
            // Iterasi melalui semua plot dalam item
            Object.values(item.originalData.plot).forEach(plot => {
                if (plot.data_entry) {
                    // Format nilai-nilai yang diperlukan
                    const normal_bb_pb = formatAngka(plot.data_entry.berbunga?.normal_bb_pb) || "0";
                    const normal_m_pb = plot.data_entry.mandul?.normal_m_pb || "0";
                    const total_pb = formatAngka(plot.data_entry.total_pb) || "0";
                    
                    // Hitung persentase n_per_n dan n_per_t dengan 2 angka di belakang koma
                    const n_per_n = hitungPersentase(normal_bb_pb, (parseFloat(normal_bb_pb) + parseFloat(normal_m_pb)));
                    const n_per_t = hitungPersentase(normal_bb_pb, total_pb);

                    // Pinggiran Start
                    const total_p_pb = formatAngka(plot.data_entry.total_p_pb) || "0";
                    // Pinggiran End

                    const plotData = {
                        // Info Lokasi
                        lokasi: item.lokasi || "-",
                        plot: plot.plot || "-",
                        luas: plot.luas_plot || "-",
                        pjp: plot.pjp || "-",
                        
                        // Fase Bunga Normal
                        open_heart_fbn_pb: plot.data_entry.fase_bunga_normal?.open_heart_fbn_pb || "0",
                        red_bud_125_fbn_pb: plot.data_entry.fase_bunga_normal?.red_bud_125_fbn_pb || "0",
                        red_bud_254_fbn_pb: plot.data_entry.fase_bunga_normal?.red_bud_254_fbn_pb || "0",
                        early_cones_fbn_pb: plot.data_entry.fase_bunga_normal?.early_cones_fbn_pb || "0",
                        mid_cones_fbn_pb: plot.data_entry.fase_bunga_normal?.mid_cones_fbn_pb || "0",
                        late_cones_fbn_pb: plot.data_entry.fase_bunga_normal?.late_cones_fbn_pb || "0",
                        
                        // Fase Bunga Alami
                        early_petals_fba_pb: plot.data_entry.fase_bunga_alami?.early_petals_fba_pb || "0",
                        mid_petals_fba_pb: plot.data_entry.fase_bunga_alami?.mid_petals_fba_pb || "0",
                        late_petals_fba_pb: plot.data_entry.fase_bunga_alami?.late_petals_fba_pb || "0",
                        dry_petals_fba_pb: plot.data_entry.fase_bunga_alami?.dry_petals_fba_pb || "0",
                        filling_stage_1_fba_pb: plot.data_entry.fase_bunga_alami?.filling_stage_1_fba_pb || "0",
                        filling_stage_2_fba_pb: plot.data_entry.fase_bunga_alami?.filling_stage_2_fba_pb || "0",
                        sc0_fba_pb: plot.data_entry.fase_bunga_alami?.sc0_fba_pb || "0",
                        sc0_ld_fba_pb: plot.data_entry.fase_bunga_alami?.sc0_ld_fba_pb || "0",
                        terpetik_fba_pb: plot.data_entry.fase_bunga_alami?.terpetik_fba_pb || "0",
                        dimakan_tikus_fba_pb: plot.data_entry.fase_bunga_alami?.dimakan_tikus_fba_pb || "0",
                        
                        // Berbunga - DIFORMAT untuk menghapus angka di belakang titik
                        normal_bb_pb: normal_bb_pb,
                        phy_pbb_pb: plot.data_entry.berbunga?.penyakit_berbunga?.phy_pbb_pb || "0",
                        erw_pbb_pb: plot.data_entry.berbunga?.penyakit_berbunga?.erw_pbb_pb || "0",
                        kerdil_bb_pb: plot.data_entry.berbunga?.kerdil_bb_pb || "0",
                        
                        // Buah Alami - DIFORMAT untuk menghapus angka di belakang titik
                        buah_alami_pb: formatAngka(plot.data_entry.buah_alami_pb) || "0",
                        
                        // Mati
                        mati_pb: plot.data_entry.mati_pb || "0",
                        
                        // Mandul
                        normal_m_pb: normal_m_pb,
                        phy_pm_pb: plot.data_entry.mandul?.penyakit_mandul?.phy_pm_pb || "0",
                        erw_pm_pb: plot.data_entry.mandul?.penyakit_mandul?.erw_pm_pb || "0",
                        kerdil_m_pb: plot.data_entry.mandul?.kerdil_m_pb || "0",
                        
                        // Total - DIFORMAT untuk menghapus angka di belakang titik
                        total_pb: total_pb,
                        
                        // Spot Mati
                        spot_mati_pb: plot.data_entry.spot_mati_pb || "0",
                        
                        // % Bunga Normal - DIHITUNG berdasarkan rumus
                        n_per_n: n_per_n,
                        n_per_t: n_per_t,
                        
                        // Populasi/Ha - DIFORMAT dengan pemisah ribuan
                        normal_pb: formatAngkaRibuan(plot.data_entry.populasi?.normal_pb) || "0",
                        total_populasi_pb: formatAngkaRibuan(plot.data_entry.populasi?.total_pb) || "0",

                        // Pinggiran Start
                        // Berbunga
                        normal_bbp_pb: plot.data_entry.berbunga_pinggiran?.normal_bbp_pb || "0",
                        phy_pbbp_pb: plot.data_entry.berbunga_pinggiran?.penyakit_berbunga_pinggiran?.phy_pbbp_pb || "0",
                        erw_pbbp_pb: plot.data_entry.berbunga_pinggiran?.penyakit_berbunga_pinggiran?.erw_pbbp_pb || "0",
                        kerdil_bbp_pb: plot.data_entry.berbunga_pinggiran?.kerdil_bbp_pb || "0",

                        // Buah Alami
                        buah_alami_p_pb: plot.data_entry.buah_alami_p_pb || "0",

                        // Mati
                        mati_p_pb: plot.data_entry.mati_p_pb || "0",

                        // Mandul
                        normal_mp_pb: plot.data_entry.mandul_pinggiran?.normal_mp_pb || "0",
                        phy_pmp_pb: plot.data_entry.mandul_pinggiran?.penyakit_mandul_pinggiran?.phy_pmp_pb || "0",
                        erw_pmp_pb: plot.data_entry.mandul_pinggiran?.penyakit_mandul_pinggiran?.erw_pmp_pb || "0",
                        kerdil_mp_pb: plot.data_entry.mandul_pinggiran?.kerdil_mp_pb || "0",

                        //Total
                        total_p_pb: total_p_pb,
                        // Pinggiran End
                    };
                    
                    allPlots.push(plotData);
                }
            });
        }
    });

    // URUTKAN DATA PLOT BERDASARKAN NOMOR PLOT
    return allPlots.sort((a, b) => {
        const plotA = parseInt(a.plot) || 0;
        const plotB = parseInt(b.plot) || 0;
        return plotA - plotB;
    });
};

// Fungsi untuk mendapatkan signature dari tb_user berdasarkan uid
export const getSignatureFromUser = async (uid) => {
    if (!uid) {
        console.log('UID tidak ditemukan');
        return null;
    }

    try {
        // Referensi ke tb_user
        const userRef = ref(database, "db_user/tb_user");
        
        // Ambil semua data user
        const snapshot = await get(userRef);
        
        if (snapshot.exists()) {
            const userData = snapshot.val();
            console.log('Data user ditemukan:', Object.keys(userData).length, 'users');
            
            // Cari user dengan uid yang sesuai
            const foundUser = Object.values(userData).find(
                user => user.uid === uid
            );
            
            if (foundUser) {
                console.log('User ditemukan:', foundUser.name, 'UID:', foundUser.uid);
                if (foundUser.signatureBase64) {
                    console.log('Signature ditemukan, panjang:', foundUser.signatureBase64.length);
                    return foundUser.signatureBase64;
                } else {
                    console.log('Signature tidak ditemukan untuk user:', foundUser.name);
                }
            } else {
                console.log('User dengan UID', uid, 'tidak ditemukan');
            }
        } else {
            console.log('Tidak ada data user di database');
        }
        
        return null;
    } catch (error) {
        console.error("Error fetching user signature:", error);
        return null;
    }
};

// Fungsi untuk mendapatkan uid_pengamat dan uid_mandor dari groupData
export const getUidData = (groupData) => {
    if (!groupData || !groupData.items || groupData.items.length === 0) {
        console.log('Group data tidak valid');
        return {
            uid_pengamat: null,
            uid_mandor: null
        };
    }

    // Ambil uid_pengamat dari data pertama (asumsi semua data dalam group memiliki uid yang sama)
    const uid_pengamat = groupData.items[0]?.originalData?.uid_pengamat || null;
    
    // Ambil uid_mandor dari data pertama
    const uid_mandor = groupData.items[0]?.originalData?.uid_mandor || null;

    console.log('UID Pengamat:', uid_pengamat);
    console.log('UID Mandor:', uid_mandor);

    return {
        uid_pengamat,
        uid_mandor
    };
};

// Fungsi utama untuk mendapatkan semua data form (diperbarui untuk include plot data)
export const getFormData = async (groupData) => {
    const statusLokasi = await getStatusLokasi(groupData);
    const plotDataArray = getAllPlotData(groupData);
    
    // Dapatkan uid data
    const { uid_pengamat, uid_mandor } = getUidData(groupData);
    
    // Dapatkan signature
    const signaturePengamat = await getSignatureFromUser(uid_pengamat);
    const signatureMandor = await getSignatureFromUser(uid_mandor);
    
    return {
        tanggalPengamatan: getTanggalPengamatan(groupData),
        bagianUnitKerja: getBagianUnitKerja(groupData),
        ppn: getPPN(groupData),
        statusLokasi: statusLokasi,
        namaPengamat: getNamaPengamat(groupData),
        namaPengamatPertama: getNamaPengamatPertama(groupData),
        namaMandor: getNamaMandor(groupData),
        plotData: plotDataArray,
        signaturePengamat: signaturePengamat, // Tambahkan signature pengamat
        signatureMandor: signatureMandor // Tambahkan signature mandor
    };
};